import React, { useState } from 'react';
import { CASING_DB, SECTIONS, UNIT_TO_G } from '../data/ingredients';
import styles from './RecipeStats.module.css';

export default function RecipeStats({ calc, ingDB, onOpenPricing }) {
  const {
    totals, nutrition, meatWeightG, filledIngredients, casingLines,
    typicalLinkWeightG,
    equipLoss, setEquipLoss,
    cookLossPct, setCookLossPct,
    selectedCasing, setSelectedCasing,
    linkLength, setLinkLength,
    linkWeight, setLinkWeight,
    userFtPerLb, setUserFtPerLb,
    casingResult,
  } = calc;

  const [openSection, setOpenSection] = useState({
    breakdown: true,
    formula: true,
    nutrition: true,
    casing: true,
    cost: false,
  });

  const toggle = (key) =>
    setOpenSection(prev => ({ ...prev, [key]: !prev[key] }));

  const stuffable = totals?.stuffable || 0;
  const casing    = selectedCasing !== null ? CASING_DB[selectedCasing] : null;
  // Reference note from the live DB (user may have edited the ft/lb there)
  const liveCasingEntry = ingDB && selectedCasing !== null
    ? ingDB.db.casing?.find(c => c.name === CASING_DB[selectedCasing]?.[0])
    : null;

  return (
    <div className={styles.sidebar}>

      {/* ── INGREDIENT BREAKDOWN ── */}
      <Section
        title="Ingredient Breakdown"
        open={openSection.breakdown}
        onToggle={() => toggle('breakdown')}
      >
        {filledIngredients && filledIngredients.length > 0 ? (
          <IngredientBreakdown
            rows={filledIngredients}
            meatWeightG={meatWeightG}
          />
        ) : (
          <p className={styles.empty}>Add ingredients to see breakdown.</p>
        )}
      </Section>

      {/* ── FORMULA TOTALS ── */}
      <Section
        title="Formula Totals"
        open={openSection.formula}
        onToggle={() => toggle('formula')}
      >
        <StatRow label="Total Weight"
          value={`${totals?.totalG?.toFixed(1) ?? '—'} g`}
          sub={`${totals?.totalLb?.toFixed(3) ?? '—'} lbs`} />

        <div className={styles.editRow}>
          <span className={styles.editLabel}>Equip. Loss</span>
          <div className={styles.editInput}>
            <input
              type="text"
              inputMode="decimal"
              value={equipLoss}
              onChange={e => setEquipLoss(parseFloat(e.target.value) || 0)}
              className={styles.lossInput}
            />
            <span className={styles.editUnit}>g</span>
          </div>
        </div>

        <StatRow
          label="Stuffable"
          value={`${stuffable.toFixed(1)} g`}
          sub={`${totals?.stuffLb?.toFixed(3) ?? '—'} lbs`}
          highlight
        />

        {/* Cook / smoke loss — what actually comes out of the smoker */}
        <div className={styles.editRow}>
          <span className={styles.editLabel}>Cook Loss</span>
          <div className={styles.editInput}>
            <input
              type="text"
              inputMode="decimal"
              value={cookLossPct}
              onChange={e => {
                const v = e.target.value;
                setCookLossPct(v === '' ? 0 : Math.min(99, Math.max(0, parseFloat(v) || 0)));
              }}
              className={styles.lossInput}
            />
            <span className={styles.editUnit}>%</span>
          </div>
        </div>

        <StatRow
          label="Cooked Weight"
          value={`${totals?.cookedLb?.toFixed(3) ?? '—'} lbs`}
          sub={`${totals?.cookedG?.toFixed(1) ?? '—'} g`}
          highlight
        />
        {stuffable > 0 && (
          <p className={styles.cookRange}>
            Typical 20–30% loss →{' '}
            {totals?.cookedLoLb?.toFixed(2)}–{totals?.cookedHiLb?.toFixed(2)} lbs
          </p>
        )}

        {/* Meat+Fat sub-total */}
        {totals?.mfPct > 0 && (
          <div className={styles.subRow}>
            <span className={styles.subLabel}>Meat + Fat</span>
            <span className={styles.subValues}>
              <span className={`${styles.subPct} ${
                Math.abs(totals.mfPct - 100) < 0.01 ? styles.good :
                totals.mfPct > 100 ? styles.danger : styles.warn
              }`}>{Math.round(totals.mfPct)}%</span>
              <span className={styles.subG}>{totals?.mfG?.toFixed(0) ?? '—'}g</span>
            </span>
          </div>
        )}
        {/* Additions sub-total */}
        {totals?.additionsPct > 0 && (
          <div className={styles.subRow}>
            <span className={styles.subLabel}>Additions</span>
            <span className={styles.subValues}>
              <span className={styles.subPct}>{totals.additionsPct.toFixed(1)}%</span>
              <span className={styles.subG}>{totals?.additionsG?.toFixed(0) ?? '—'}g</span>
            </span>
          </div>
        )}
      </Section>

      {/* ── NUTRITION ── */}
      <Section
        title="Nutrition Estimate"
        subtitle="per 100g · pre-cook"
        open={openSection.nutrition}
        onToggle={() => toggle('nutrition')}
      >
        {meatWeightG > 0 && nutrition ? (
          <>
            <MacroBar label="Protein"  value={nutrition.protein}  unit="g" max={40} />
            <MacroBar label="Carbs"    value={nutrition.carbs}    unit="g" max={20} />
            <MacroBar label="Fat"      value={nutrition.fat}      unit="g" max={60} />
            <MacroBar label="Sodium"   value={nutrition.sodium}   unit="mg" max={2000} />
            <MacroBar label="Calories" value={nutrition.calories} unit="kcal" max={600} />

            <div className={styles.fatRatioGroup}>
              <div className={styles.fatRatioItem}>
                <span className={styles.fatRatioLabel}>Fat (total sausage)</span>
                <span className={styles.fatRatioVal}>
                  {nutrition.fatRatioTotal.toFixed(1)}%
                </span>
              </div>
              <div className={styles.fatRatioItem}>
                <span className={styles.fatRatioLabel}>Fat (meat+fat only)</span>
                <span className={`${styles.fatRatioVal} ${styles.accent}`}>
                  {nutrition.fatRatioMFOnly.toFixed(1)}%
                </span>
              </div>
            </div>

            {nutrition.unknownPct > 0 && (
              <p className={styles.coverageWarn}>
                ⚠️ Based on {nutrition.coverage}% of the mix by weight.
                No macro data for {nutrition.unknownNames.join(', ')} — add it in
                🗂️ Ingredients for a complete estimate.
              </p>
            )}
            <p className={styles.disclaimer}>
              USDA FoodData Central · raw weight estimate
            </p>
          </>
        ) : (
          <p className={styles.empty}>Enter ingredients to see estimates.</p>
        )}
      </Section>

      {/* ── CASING CALCULATOR ── */}
      <Section
        title="Casing Calculator"
        subtitle="pre-cook · estimate only"
        open={openSection.casing}
        onToggle={() => toggle('casing')}
      >
        {/* Casing select */}
        <div className={styles.casingField}>
          <label className={styles.statLabel}>Casing</label>
          <select
            className={styles.casingSelect}
            value={selectedCasing ?? ''}
            onChange={e => setSelectedCasing(
              e.target.value === '' ? null : parseInt(e.target.value)
            )}
          >
            <option value="">— select —</option>
            {CASING_DB.map((c, i) => (
              <option key={i} value={i}>{c[0]}</option>
            ))}
          </select>
          {casing && (
            <p className={styles.prepNote}>{casing[3]}</p>
          )}
          {liveCasingEntry?.note && (
            <p className={styles.casingRefNote}>
              📏 Reference: {liveCasingEntry.note}
              {liveCasingEntry.note !== liveCasingEntry.note && ''}
            </p>
          )}
        </div>

        {/* Link length + weight */}
        <div className={styles.casingInputRow}>
          <div className={styles.casingField}>
            <label className={styles.statLabel}>Link Length</label>
            <div className={styles.inlineInput}>
              <input type="text" inputMode="decimal"
                className={styles.miniInput} value={linkLength}
                onChange={e => setLinkLength(parseFloat(e.target.value) || 6)} />
              <span className={styles.editUnit}>in</span>
            </div>
          </div>
          <div className={styles.casingField}>
            <label className={styles.statLabel}>Avg Sausage Wt</label>
            <div className={styles.inlineInput}>
              <input type="text" inputMode="decimal"
                className={styles.miniInput} placeholder="auto"
                value={linkWeight} onChange={e => setLinkWeight(e.target.value)} />
              <span className={styles.editUnit}>g</span>
            </div>
          </div>
        </div>

        {/* Ft/lb override only — casing cost is for Cost Estimator, set in Ingredients DB */}
        <div className={styles.casingField}>
          <label className={styles.statLabel}>My ft/lb (override)</label>
          <div className={styles.inlineInput}>
            <input type="text" inputMode="decimal"
              className={styles.miniInput}
              placeholder={casing ? String(casing[2]) : 'DB default'}
              value={userFtPerLb}
              onChange={e => setUserFtPerLb(e.target.value)} />
            <span className={styles.editUnit}>ft/lb</span>
          </div>
          <p className={styles.overrideHint}>
            Leave blank to use the DB reference value.
            Enter your real-world result to override.
          </p>
        </div>

        {/* Results */}
        {casingResult ? (
          <div className={styles.casingResults}>
            <StatRow label="Rate used"
              value={`${casingResult.ftPerLb} ft/lb`}
              sub={userFtPerLb ? 'user override' : 'DB default'} />
            {/* What the ft/lb + link length predict a raw stuffed link weighs.
                Independent of the manual Avg Sausage Wt above. */}
            <StatRow label="Predicted link wt"
              value={`${casingResult.predictedLinkWeightG.toFixed(1)} g`}
              sub={`${casingResult.predictedLinkWeightOz.toFixed(2)} oz · ${linkLength}″ raw stuffed`} />
            <StatRow label="Casing needed"
              value={`${casingResult.feetNeeded} ft`}
              sub={`${casingResult.metersNeeded} m`} />
            <StatRow label="+15% buffer"
              value={`${casingResult.feetBuffer} ft`}
              sub={`${casingResult.metersBuffer} m`}
              highlight />
            {/* What a link weighs once smoked — the packaging number */}
            <StatRow label="Cooked link wt"
              value={`${casingResult.cookedLinkG.toFixed(1)} g`}
              sub={`${casingResult.cookedLinkOz.toFixed(2)} oz · after ${casingResult.cookLossPct}% loss`} />
            <StatRow label="Est. sausages"
              value={`${casingResult.estLinks}`}
              sub={linkWeight
                ? `at ${casingResult.effectiveLinkWt.toFixed(0)}g each · your weight`
                : `at ${casingResult.effectiveLinkWt.toFixed(0)}g each · predicted`}
              highlight />

          </div>
        ) : (
          <p className={styles.empty}>
            {stuffable === 0
              ? 'Enter ingredients first.'
              : 'Select a casing above.'}
          </p>
        )}
      </Section>

      {/* ── COST ESTIMATOR ── */}
      <Section
        title="Cost Estimator"
        subtitle="set prices in 🗂️ Ingredients"
        open={openSection.cost}
        onToggle={() => toggle('cost')}
      >
        <CostEstimator
          filledIngredients={filledIngredients}
          casingLines={casingLines}
          meatWeightG={meatWeightG}
          ingDB={ingDB}
          stuffableG={stuffable}
          stuffLb={totals?.stuffLb || 0}
          casingResult={casingResult}
          typicalLinkWeightG={typicalLinkWeightG}
          onOpenPricing={onOpenPricing}
        />
      </Section>

    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function IngredientBreakdown({ rows, meatWeightG }) {
  // Group by section
  const grouped = {};
  const sectionOrder = SECTIONS.map(s => s.key);
  rows.forEach(row => {
    if (!grouped[row.sectionKey]) grouped[row.sectionKey] = [];
    grouped[row.sectionKey].push(row);
  });

  return (
    <div className={styles.breakdown}>
      {sectionOrder.map(key => {
        const sectRows = grouped[key];
        if (!sectRows || sectRows.length === 0) return null;
        const sect = SECTIONS.find(s => s.key === key);
        return (
          <div key={key} className={styles.bSection}>
            <div className={styles.bSectionLabel} style={{ color: sect?.color || 'var(--accent)' }}>
              {sect?.label || key}
            </div>
            {sectRows.map(row => {
              const pct   = parseFloat(row.pct) || 0;
              const grams = meatWeightG > 0 ? Math.round(pct / 100 * meatWeightG * 10) / 10 : 0;
              return (
                <div key={row.id} className={styles.bRow}>
                  <span className={styles.bName}>{row.name}</span>
                  <span className={styles.bPct}>{pct.toFixed(2)}%</span>
                  <span className={styles.bG}>{grams.toFixed(1)}g</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}


function Section({ title, subtitle, open, onToggle, children }) {
  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={onToggle}>
        <div className={styles.sectionTitleGroup}>
          <span className={styles.sectionTitle}>{title}</span>
          {subtitle && <span className={styles.sectionSub}>{subtitle}</span>}
        </div>
        <span className={styles.chevron}>{open ? '⌄' : '›'}</span>
      </button>
      {open && (
        <div className={styles.sectionBody}>
          {children}
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value, sub, highlight }) {
  return (
    <div className={`${styles.statRow} ${highlight ? styles.statHighlight : ''}`}>
      <span className={styles.statLabel}>{label}</span>
      <div className={styles.statValues}>
        <span className={styles.statValue}>{value}</span>
        {sub && <span className={styles.statSub}>{sub}</span>}
      </div>
    </div>
  );
}

function MacroBar({ label, value, unit, max }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className={styles.macroRow}>
      <span className={styles.macroLabel}>{label}</span>
      <div className={styles.macroTrack}>
        <div className={styles.macroFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={styles.macroVal}>
        {value.toFixed(1)}
        <span className={styles.macroUnit}>{unit}</span>
      </span>
    </div>
  );
}

// ── CostEstimator sub-component ─────────────────────────────
function CostEstimator({ filledIngredients, casingLines, meatWeightG, ingDB,
                         stuffableG, stuffLb, casingResult, typicalLinkWeightG,
                         onOpenPricing }) {

  const hasIngredients = meatWeightG && filledIngredients && filledIngredients.length > 0;
  const hasCasings     = casingLines && casingLines.length > 0;

  if (!hasIngredients && !hasCasings) {
    return <p className={styles.empty}>Add ingredients to see cost estimate.</p>;
  }

  let totalCost = 0;
  const rows = [];

  (filledIngredients || []).forEach(ing => {
    if (!ing.name || !ing.pct) return;
    const pct     = parseFloat(ing.pct) || 0;
    const grams   = pct / 100 * meatWeightG;
    const cpg     = ingDB ? ingDB.costPerGram(ing.sectionKey, ing.name) : 0;
    const cost    = cpg * grams;
    if (cpg > 0) {
      totalCost += cost;
      const ingObj  = ingDB ? ingDB.getIngredient(ing.sectionKey, ing.name) : null;
      const unit    = ingObj ? ingObj.costUnit : 'g';
      const unitAmt = grams / (UNIT_TO_G[unit] || 1);
      rows.push({ name: ing.name, grams, cost, unitAmt, unit });
    }
  });

  // ── Casing cost: from the Casings rows, priced by length ────────────────
  // The length is whatever you entered in the Casings section (usually pulled
  // from the Casing Calculator's +15% buffer). Nothing here touches weight.
  const casingRows = [];
  (casingLines || []).forEach(line => {
    const feet   = parseFloat(line.feet) || 0;
    const entry  = ingDB ? ingDB.getIngredient('casing', line.name) : null;
    if (!entry || !(entry.costPerUnit > 0) || feet <= 0) return;
    const perFoot = entry.costPerUnit / (entry.costUnit === 'yd' ? 3 : 1);
    const cost    = perFoot * feet;
    totalCost += cost;
    casingRows.push({ name: line.name, feet, cost, perFoot });
  });

  const unpricedCasings = (casingLines || []).length - casingRows.length;
  const hasCosts = rows.length > 0 || casingRows.length > 0;

  // Cost per pound of stuffable mix — the weight that actually goes in a casing
  const costPerLb = stuffLb > 0 && totalCost > 0 ? totalCost / stuffLb : 0;

  // Prefer the casing calculator's link count; fall back to the typical weight
  const estLinks = casingResult?.estLinks > 0
    ? casingResult.estLinks
    : (typicalLinkWeightG > 0 && stuffableG > 0
        ? Math.floor(stuffableG / typicalLinkWeightG) : 0);

  return (
    <div className={styles.costWrap}>
      {!hasCosts ? (
        <p className={styles.costHint}>
          Set ingredient prices in <strong>🗂️ Ingredients</strong> to see cost breakdown.
        </p>
      ) : (
        <>
          {rows.map((r, i) => (
            <div key={i} className={styles.costRow}>
              <span className={styles.costName}>{r.name}</span>
              <span className={styles.costAmt}>
                {r.unitAmt.toFixed(2)} {r.unit}
              </span>
              <span className={styles.costVal}>${r.cost.toFixed(2)}</span>
            </div>
          ))}
          {casingRows.map((c, i) => (
            <div key={`casing-${i}`} className={styles.costRow}>
              <span className={styles.costName}>{c.name}</span>
              <span className={styles.costAmt}>{c.feet.toFixed(1)} ft</span>
              <span className={styles.costVal}>${c.cost.toFixed(2)}</span>
            </div>
          ))}
          <div className={styles.costTotal}>
            <span>Total batch cost</span>
            <span>${totalCost.toFixed(2)}</span>
          </div>

          {costPerLb > 0 && (
            <div className={styles.costPerLink}>
              <span>Cost per lb <span className={styles.costPerLbNote}>stuffable</span></span>
              <span className={styles.costPerLinkVal}>${costPerLb.toFixed(2)}</span>
            </div>
          )}

          {estLinks > 0 && totalCost > 0 && (
            <div className={styles.costPerLink}>
              <span>Cost per sausage</span>
              <span className={styles.costPerLinkVal}>
                ${(totalCost / estLinks).toFixed(2)}
              </span>
            </div>
          )}

          {onOpenPricing && costPerLb > 0 && (
            <button className={styles.pricingLink} onClick={onOpenPricing}>
              <span>💵 Price this for sale</span>
              <span className={styles.pricingLinkHint}>
                cost per lb is materials only — add labor, yield loss and overhead
              </span>
            </button>
          )}

          {rows.length < filledIngredients.length && (
            <p className={styles.costPartial}>
              {filledIngredients.length - rows.length} ingredient{filledIngredients.length - rows.length !== 1 ? 's' : ''} without price — set in 🗂️ Ingredients
            </p>
          )}
          {unpricedCasings > 0 && (
            <p className={styles.costPartial}>
              {unpricedCasings} casing{unpricedCasings !== 1 ? 's' : ''} without price or length — set in 🗂️ Ingredients
            </p>
          )}
        </>
      )}
    </div>
  );
}
